import { useQuery, type QueryClient } from "@tanstack/react-query";

import { AgentRunStatus } from "@/src/models/agent-run";
import { getTaskAgentMessages, getTaskAgentRun } from "@/src/requests/agent-run";

export const TASK_AGENT_RUN_CACHE_KEY = "task-agent-run";
export const TASK_AGENT_MESSAGES_CACHE_KEY = "task-agent-messages";
export const TASK_AGENT_RUN_STALE_TIME_MS = 1000 * 30;
export const TASK_AGENT_RUN_POLL_INTERVAL_MS = 1000 * 5;

const ACTIVE_AGENT_RUN_STATUSES: AgentRunStatus[] = [
  AgentRunStatus.QUEUED,
  AgentRunStatus.PROCESSING,
  AgentRunStatus.MATERIALIZING,
];

export function isAgentRunStatusActive(status: AgentRunStatus | null | undefined): boolean {
  return status !== null && status !== undefined && ACTIVE_AGENT_RUN_STATUSES.includes(status);
}

// Whether the agent for this task is still actively running
export function isTaskAgentRunActive(queryClient: QueryClient, taskId: string): boolean {
  const data = queryClient.getQueryData<Awaited<ReturnType<typeof getTaskAgentRun>>>([
    TASK_AGENT_RUN_CACHE_KEY,
    taskId,
  ]);
  const status = data?.agentRun?.status;

  return isAgentRunStatusActive(status);
}

export function useTaskAgentRun(taskId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [TASK_AGENT_RUN_CACHE_KEY, taskId],
    queryFn: () => getTaskAgentRun(taskId),
    enabled,
    staleTime: TASK_AGENT_RUN_STALE_TIME_MS,
    refetchInterval: (query) => {
      const status = query.state.data?.agentRun?.status;

      return isAgentRunStatusActive(status) ? TASK_AGENT_RUN_POLL_INTERVAL_MS : false;
    },
  });
}

export function useTaskAgentMessages(taskId: string, { enabled, poll }: { enabled: boolean; poll: boolean }) {
  return useQuery({
    queryKey: [TASK_AGENT_MESSAGES_CACHE_KEY, taskId],
    queryFn: () => getTaskAgentMessages(taskId),
    enabled,
    staleTime: TASK_AGENT_RUN_STALE_TIME_MS,
    refetchInterval: poll ? TASK_AGENT_RUN_POLL_INTERVAL_MS : false,
  });
}
